import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Car,
  Phone,
  Mail,
  FileCheck,
  Ban,
} from "lucide-react";
import DataTablePagination from "@/components/ui/data-table-pagination";
import { useDrivers, useDeleteDriver, useToggleDriverActiveStatus } from "@/api-services/hooks/useDriver";
import { DriverDTO, PagedData } from "@/api-services/hooks/adminTypes";
import DeleteDriverConfirmation from "@/components/drivers/DeleteDriverConfirmation";
import KycStatusBadge from "@/components/drivers/KycStatusBadge";

const DriversPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kycFilter, setKycFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete functionality state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<{ id: number; name: string; email?: string } | null>(null);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [statusFilter, kycFilter, activeFilter, searchTerm]);

  // Delete driver hook
  const { mutate: deleteDriver, isPending: isDeleting } = useDeleteDriver({
    onSuccess: async (data, variables) => {
      toast.success("Driver deleted successfully! Corresponding user account has also been removed.");
      // Invalidate all drivers queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
    onError: (error, variables) => {
      toast.error(`Failed to delete driver: ${error.message}`);
    },
  });

  // Toggle active status hook
  const { mutate: toggleActiveStatus, isPending: isTogglingStatus } = useToggleDriverActiveStatus({
    onSuccess: async (data, driverId) => {
      toast.success("Driver status updated successfully!");

      // Since the API returns null data, we need to refetch to get the actual state
      // The optimistic update already showed the expected state, so we need to verify it

      // Create the query parameters object that matches what useDrivers uses
      const queryParams = {
        page: currentPage,
        size: itemsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== "all" && { status: statusFilter }),
        ...(kycFilter !== "all" && { kycStatus: kycFilter }),
        ...(activeFilter !== "all" && { isActive: activeFilter === "active" }),
      };

      // Instead of using the API response (which is null), refetch to get accurate data
      await queryClient.invalidateQueries({ queryKey: ['drivers', queryParams] });
    },
    onError: (error, driverId) => {
      toast.error(`Failed to update driver status: ${error.message}`);

      // Create the query parameters object that matches what useDrivers uses
      const queryParams = {
        page: currentPage,
        size: itemsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== "all" && { status: statusFilter }),
        ...(kycFilter !== "all" && { kycStatus: kycFilter }),
        ...(activeFilter !== "all" && { isActive: activeFilter === "active" }),
      };

      // Revert optimistic update on error - refetch to get original state
      queryClient.invalidateQueries({ queryKey: ['drivers', queryParams] });
    },
  });

  // Helper function to handle driver status toggle
  const handleDriverStatusToggle = (driver: DriverDTO) => {
    // Ensure driverId exists before proceeding
    if (!driver.driverId) {
      toast.error("Driver ID is missing. Cannot update status.");
      return;
    }

    const currentStatus = driver.isActive ?? true;
    const driverId = driver.driverId;

    // Create the query parameters object that matches what useDrivers uses
    const queryParams = {
      page: currentPage,
      size: itemsPerPage,
      ...(searchTerm && { search: searchTerm }),
      ...(statusFilter !== "all" && { status: statusFilter }),
      ...(kycFilter !== "all" && { kycStatus: kycFilter }),
      ...(activeFilter !== "all" && { isActive: activeFilter === "active" }),
    };

    // Optimistic update - immediately update the UI
    const newStatus = !currentStatus;
    queryClient.setQueryData(
      ['drivers', queryParams],
      (oldData: any) => {
        if (!oldData?.content) return oldData;

        const updatedContent = oldData.content.map((d: any) =>
          d.driverId === driverId
            ? { ...d, isActive: newStatus }
            : d
        );

        return {
          ...oldData,
          content: updatedContent,
        };
      }
    );

    // Store the expected new status for error recovery
    const expectedStatus = newStatus;

    // Call the API
    toggleActiveStatus(driverId);
  };

  // Fetch drivers from API with all filters
  const { data, isLoading: loading, error, refetch } = useDrivers(
    {
      page: currentPage,
      size: itemsPerPage,
      ...(searchTerm && { search: searchTerm }),
      ...(statusFilter !== "all" && { status: statusFilter }),
      ...(kycFilter !== "all" && { kycStatus: kycFilter }),
      ...(activeFilter !== "all" && { isActive: activeFilter === "active" }),
    },
    {
      onError: (err) => {
        toast.error(`Failed to load drivers: ${err.message}`);
      },
    }
  );

  const getStatusBadge = (status: string) => {
    const statusLower = status?.toLowerCase() || "offline";
    const variants: Record<string, { variant: string; className: string }> = {
      online: {
        variant: "default",
        className: "bg-success text-success-foreground",
      },
      active: {
        variant: "default",
        className: "bg-success text-success-foreground",
      },
      busy: {
        variant: "secondary",
        className: "bg-warning text-warning-foreground",
      },
      offline: {
        variant: "secondary",
        className: "bg-muted text-muted-foreground",
      },
      inactive: {
        variant: "secondary",
        className: "bg-muted text-muted-foreground",
      },
      suspended: {
        variant: "destructive",
        className: "bg-destructive text-destructive-foreground",
      },
      pending: {
        variant: "secondary",
        className: "bg-muted text-muted-foreground",
      },
    };
    return variants[statusLower] || variants.offline;
  };

  const handleDriverAction = (
    action: string,
    driverId: number,
    driverName: string
  ) => {
    if (action === "Edit Details") {
      navigate(`/admin/drivers/create?driverId=${driverId}`);
    } else {
      toast.success(`${action} action completed for ${driverName}`);
    }
  };

  // Delete handler functions
  const handleDeleteClick = (driverId: number, driverName: string, driverEmail?: string) => {
    setDriverToDelete({ id: driverId, name: driverName, email: driverEmail });
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!driverToDelete) return;

    deleteDriver({
      driverId: driverToDelete.id,
      email: driverToDelete.email,
    });
    setDeleteConfirmationOpen(false);
    setDriverToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setDriverToDelete(null);
  };

  // Get drivers directly from API (filtering is now done server-side)
  const drivers = useMemo(() => {
    const driverData = data as PagedData<DriverDTO> | undefined;
    if (!driverData || !driverData.content) return [];
    return driverData.content;
  }, [data]);

  // Pagination calculations from API response
  const driverData = data as PagedData<DriverDTO> | undefined;
  const totalItems = driverData?.page?.totalElements ?? 0;

  const totalPages = driverData?.page?.totalPages ?? 0;

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  // Convert driver DTO to display format
  const getDriverDisplayName = (driver: DriverDTO) => {
    return driver.fullName || driver.email || "Unknown Driver";
  };

  const getDriverEmail = (driver: DriverDTO) => {
    return driver.email || "N/A";
  };

  const getDriverPhone = (driver: DriverDTO) => {
    return driver.phoneNumber || "N/A";
  };

  const getDriverVehicle = (driver: DriverDTO) => {
    if (driver.vehicleMake && driver.vehicleModel && driver.vehicleYear) {
      return `${driver.vehicleMake} ${driver.vehicleModel} ${driver.vehicleYear}`;
    }
    return driver.vehicleType || "N/A";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading drivers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Drivers
            </h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Driver Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage and monitor your driver network
          </p>
        </div>
        <Button
          className="bg-gradient-primary hover:opacity-90"
          onClick={() => navigate("/admin/drivers/create")}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Driver
        </Button>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search drivers by name or email..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-[140px]">
                  <Filter className="w-4 h-4 mr-2" />
                  {statusFilter === "all"
                    ? "Status"
                    : statusFilter.charAt(0).toUpperCase() +
                    statusFilter.slice(1)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => setStatusFilter("all")}>
                  All Status
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setStatusFilter("online")}>
                  Online
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setStatusFilter("busy")}>
                  Busy
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setStatusFilter("offline")}>
                  Offline
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setStatusFilter("suspended")}>
                  Suspended
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-[140px]">
                  <FileCheck className="w-4 h-4 mr-2" />
                  {kycFilter === "all" ? "KYC" : kycFilter}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => setKycFilter("all")}>
                  All KYC
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setKycFilter("PENDING")}>
                  Pending
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setKycFilter("APPROVED")}>
                  Approved
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setKycFilter("REJECTED")}>
                  Rejected
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setKycFilter("KEEP_IN_VIEW")}>
                  Keep in View
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-[140px]">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {activeFilter === "all"
                    ? "Active"
                    : activeFilter === "active"
                      ? "Active Only"
                      : "Inactive Only"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => setActiveFilter("all")}>
                  All
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setActiveFilter("active")}>
                  Active Only
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setActiveFilter("inactive")}>
                  Inactive Only
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Drivers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Drivers ({totalItems})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>KYC Status</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Total Rides</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Earnings</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drivers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No drivers found
                    </TableCell>
                  </TableRow>
                ) : (
                  drivers.map((driver) => {
                    const status =
                      driver.driverStatus ||
                      driver.availabilityStatus ||
                      "offline";
                    const statusBadge = getStatusBadge(status);
                    const displayName = getDriverDisplayName(driver);
                    const initials = displayName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2);
                    return (
                      <TableRow
                        key={driver.driverId}
                        className="hover:bg-muted/50"
                      >
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Avatar>
                              <AvatarFallback>{initials}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{displayName}</p>
                              <p className="text-sm text-muted-foreground">
                                ID: {driver.driverId}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center text-sm">
                              <Mail className="w-3 h-3 mr-1 text-muted-foreground" />
                              {getDriverEmail(driver)}
                            </div>
                            <div className="flex items-center text-sm">
                              <Phone className="w-3 h-3 mr-1 text-muted-foreground" />
                              {getDriverPhone(driver)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <KycStatusBadge status={driver.kycStatus || "PENDING"} size="sm" />
                        </TableCell>
                        <TableCell>
                          <Switch
                            data-testid={`driver-active-switch-${driver.driverId}`}
                            checked={driver.isActive ?? true}
                            disabled={isTogglingStatus}
                            onCheckedChange={() => handleDriverStatusToggle(driver)}
                          />
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            {status.toLowerCase() === "online" && (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            )}
                            {status.toLowerCase() === "active" && (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            )}
                            {status.toLowerCase() === "busy" && (
                              <Clock className="w-3 h-3 mr-1" />
                            )}
                            {status.toLowerCase() === "offline" && (
                              <XCircle className="w-3 h-3 mr-1" />
                            )}
                            {status.toLowerCase() === "inactive" && (
                              <XCircle className="w-3 h-3 mr-1" />
                            )}
                            {status.toLowerCase() === "suspended" && (
                              <Ban className="w-3 h-3 mr-1" />
                            )}
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <Star className="w-4 h-4 text-chart-accent mr-1" />
                            {driver.averageRating?.toFixed(1) || "N/A"}
                          </div>
                        </TableCell>
                        <TableCell>
                          {driver.totalRidesCompleted?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {getDriverVehicle(driver)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {driver.vehicleLicensePlate || ""}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          ${driver.totalEarnings?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDriverAction(
                                    "Edit Details",
                                    driver.driverId || 0,
                                    displayName
                                  )
                                }
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => navigate(`/admin/drivers/${driver.driverId}`)}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => navigate(`/admin/drivers/${driver.driverId}?tab=kyc`)}
                              >
                                <FileCheck className="w-4 h-4 mr-2" />
                                KYC Review
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteClick(
                                    driver.driverId || 0,
                                    displayName,
                                    getDriverEmail(driver)
                                  )
                                }
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Driver
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {drivers.length > 0 && totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Driver Confirmation Modal */}
      <DeleteDriverConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        driverName={driverToDelete?.name || ""}
        driverId={driverToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default DriversPage;
